import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  approveCampaign,
  CampaignAudience,
  CampaignChannel,
  CampaignEvent,
  CampaignLang,
  CampaignPerson,
  CampaignText,
  commentCampaign,
  deleteCampaignComment,
  editCampaignComment,
  getCampaignDetail,
  getCampaignPreviewHtml,
  getCampaignStaff,
  MarketingCampaign,
  rejectCampaign,
  reviewCampaignWithAi,
  revokeCampaignApproval,
  sendCampaign,
  setCampaignReviewers,
  updateCampaign
} from '../../../api/services/marketingService';
import {
  ago, AUDIENCE_LABEL, Avatar, CHANNEL_LABEL, emptyContent, emptyText, formatDateTime, inputClass, KIND_LABEL, KIND_TONE,
  Label, LANG_NAME, LANGS, ref, SELECTABLE_AUDIENCES, StateBadge
} from './shared';

/**
 * Une campagne, comme une merge request GitLab : en-tête, onglets (vue
 * d'ensemble, contenu, aperçu), bloc d'approbation, bloc d'envoi (le
 * « merge »), fil d'activité avec diffs et relectures de l'IA, commentaires
 * avec @mentions, et une barre latérale pour les relecteurs (des personnes et
 * l'IA). L'en-tête et la barre latérale restent visibles au défilement, et les
 * actions (approuver, envoyer, fermer) sont en haut à droite, comme sur Azure DevOps.
 */

type Tab = 'overview' | 'content' | 'preview';
type Notice = { tone: 'ok' | 'warn' | 'error'; text: string } | null;
type Filter = 'all' | 'comments' | 'history';

const FIELD_LABEL: Record<string, string> = {
  title: 'titre du push', body: 'message du push', subject: 'objet de l’e-mail', html: 'corps de l’e-mail',
  channels: 'canaux', audience: 'audience', link: 'lien'
};

const stripHtml = (html: string) => String(html || '').replace(/<br\s*\/?>/gi, '\n').replace(/<\/p>/gi, '\n').replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').replace(/&amp;/g, '&').replace(/\n{3,}/g, '\n\n').trim();

const box = 'rounded-2xl bg-white shadow-[0_1px_2px_rgb(19_21_26/0.04)]';
const sideTitle = 'text-[12px] font-semibold text-gray-900';

interface Props {
  campaignRef: string;
  token: string;
  meId: string;
  onBack: () => void;
  onChanged: (campaign: MarketingCampaign) => void;
}

const CampaignPage = ({ campaignRef, token, meId, onBack, onChanged }: Props) => {
  const [campaign, setCampaign] = useState<MarketingCampaign | null>(null);
  const [events, setEvents] = useState<CampaignEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [notice, setNotice] = useState<Notice>(null);
  const [busy, setBusy] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [filter, setFilter] = useState<Filter>('all');
  const [staff, setStaff] = useState<CampaignPerson[]>([]);
  const [compact, setCompact] = useState(false);
  const [headerH, setHeaderH] = useState(120);
  const sentinelRef = useRef<HTMLSpanElement>(null);
  const headerRef = useRef<HTMLElement>(null);
  const timelineEndRef = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    try {
      const data = await getCampaignDetail(token, campaignRef);
      setCampaign(data.campaign);
      setEvents(data.events);
      onChanged(data.campaign);
      setError(null);
    } catch (err: any) {
      setError(err.message || 'Campagne introuvable.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, campaignRef]);

  useEffect(() => { setLoading(true); load(); }, [load]);

  // L'équipe, pour choisir les relecteurs et mentionner quelqu'un en commentaire.
  useEffect(() => { getCampaignStaff(token).then(setStaff).catch(() => undefined); }, [token]);

  // L'en-tête se resserre dès que la page défile, comme sur une pull request.
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => setCompact(!entry.isIntersecting), { threshold: 0 });
    io.observe(el);
    return () => io.disconnect();
  }, [campaign !== null]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(() => setHeaderH(el.offsetHeight));
    ro.observe(el);
    return () => ro.disconnect();
  }, [campaign !== null]); // eslint-disable-line react-hooks/exhaustive-deps

  // L'IA assignée relit le texte s'il ne l'a pas encore été (une fois par version).
  const autoReviewed = useRef<string | null>(null);
  useEffect(() => {
    if (!campaign || !campaign.ai_reviewer || campaign.ai_review_current) return;
    if (!['pending', 'approved'].includes(campaign.status)) return;
    const version = `${campaign.id_campaign}:${campaign.updated_at}`;
    if (autoReviewed.current === version) return;
    autoReviewed.current = version;
    reviewCampaignWithAi(token, campaign.id_campaign).then(load).catch(() => undefined);
  }, [campaign, token, load]);

  const act = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setNotice(null);
    try {
      await fn();
      await load();
      if (ok) setNotice({ tone: 'ok', text: ok });
    } catch (err: any) {
      setNotice({ tone: 'error', text: err.message || 'Action impossible.' });
    } finally {
      setBusy(false);
    }
  };

  if (loading && !campaign) {
    return (
      <div className="space-y-3">
        <div className="h-4 w-40 animate-pulse rounded bg-gray-100" />
        <div className="h-7 w-96 max-w-full animate-pulse rounded bg-gray-100" />
        <div className="h-64 animate-pulse rounded-2xl bg-white" />
      </div>
    );
  }
  if (error || !campaign) {
    return (
      <div className={`${box} px-4 py-12 text-center`}>
        <p className="text-sm text-red-700">{error || 'Campagne introuvable.'}</p>
        <button type="button" onClick={onBack} className="mt-3 text-sm font-medium text-indigo-600 hover:underline">← Toutes les campagnes</button>
      </div>
    );
  }

  const open = ['pending', 'approved'].includes(campaign.status);
  const isAuthor = Boolean(campaign.created_by && meId && String(campaign.created_by) === meId);
  const author = campaign.created_by ? (campaign.created_by_name || 'Un administrateur') : 'L’API Dingo';
  const channels = (campaign.channels || []).map((c) => CHANNEL_LABEL[c] || c).join(' + ');
  const comments = events.filter((e) => e.kind === 'comment').length;
  const target = campaign.audience === 'user' && campaign.target_user_name ? campaign.target_user_name : AUDIENCE_LABEL[campaign.audience];
  const canApprove = campaign.status === 'pending' && !isAuthor && (campaign.channels || []).length > 0;

  const commentActions: CommentActions = {
    meId,
    busy,
    onEdit: async (eventId, body) => {
      try {
        const data = await editCampaignComment(token, campaign.id_campaign, eventId, body);
        setEvents(data.events);
        setNotice({ tone: 'ok', text: 'Commentaire modifié.' });
        return true;
      } catch (err: any) {
        setNotice({ tone: 'error', text: err.message || 'Modification impossible.' });
        return false;
      }
    },
    onDelete: (eventId) => act(() => deleteCampaignComment(token, campaign.id_campaign, eventId), 'Commentaire supprimé.')
  };

  const relaunchReview = async () => {
    setReviewing(true);
    try { await act(() => reviewCampaignWithAi(token, campaign.id_campaign), 'Relecture terminée.'); } finally { setReviewing(false); }
  };

  return (
    <div className="pb-10">
      <span ref={sentinelRef} aria-hidden="true" className="block h-px" />

      {/* En-tête collant : titre, état et actions restent visibles au défilement */}
      <header ref={headerRef} className={`sticky -top-1 z-20 -mx-4 bg-[#F7F7FB] px-4 lg:-mx-7 lg:px-7 ${compact ? 'shadow-[0_1px_0_#E5E7EB,0_8px_16px_-12px_rgb(15_23_42/0.25)]' : ''}`}>
        <div className={`flex flex-wrap items-start gap-x-4 gap-y-3 ${compact ? 'pt-3' : 'pt-2'}`}>
          <div className="min-w-0 flex-1">
            {!compact && (
              <nav className="mb-2 text-[13px] text-gray-500">
                <button type="button" onClick={onBack} className="hover:text-indigo-700 hover:underline">Campagnes</button>
                <span className="mx-1.5 text-gray-300">/</span>
                <span className="font-mono text-gray-700">{ref(campaign)}</span>
              </nav>
            )}
            <div className="flex min-w-0 items-center gap-2.5">
              {compact && <button type="button" onClick={onBack} title="Toutes les campagnes" className="shrink-0 rounded-lg p-1 text-gray-500 hover:bg-white hover:text-gray-900"><Ico d="M15 18l-6-6 6-6" /></button>}
              <h2 className={`min-w-0 font-semibold tracking-[-0.01em] text-gray-900 ${compact ? 'truncate text-[16px]' : 'text-[22px] leading-tight'}`}>
                {campaign.content?.fr?.title || '(sans titre)'}
              </h2>
              {compact && <span className="hidden shrink-0 font-mono text-[12.5px] text-gray-400 sm:inline">{ref(campaign)}</span>}
              {compact && <span className="hidden shrink-0 sm:inline-flex"><StateBadge status={campaign.status} /></span>}
            </div>
            {!compact && (
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[13px] text-gray-600">
                <StateBadge status={campaign.status} />
                <span className="flex items-center gap-1.5">
                  <Avatar name={campaign.created_by_name} photo={campaign.created_by_photo} api={!campaign.created_by} size={20} />
                  <strong className="font-semibold text-gray-900">{author}</strong>
                </span>
                <span>{campaign.status === 'sent' ? 'a envoyé' : campaign.created_by ? 'souhaite envoyer' : 'propose d’envoyer'} à</span>
                <strong className="font-semibold text-gray-900">{target}</strong>
                <span className="text-gray-400">({channels || 'aucun canal'}) · {ago(campaign.created_at)}</span>
              </div>
            )}
          </div>

          <HeaderActions
            campaign={campaign}
            busy={busy}
            reviewing={reviewing}
            canApprove={canApprove}
            isAuthor={isAuthor}
            target={target}
            channels={channels}
            onApprove={() => act(() => approveCampaign(token, campaign.id_campaign), 'Campagne approuvée.')}
            onRevoke={() => act(() => revokeCampaignApproval(token, campaign.id_campaign), 'Approbation retirée. La campagne est de nouveau ouverte.')}
            onSend={() => act(async () => { const sent = await sendCampaign(token, campaign.id_campaign); if (sent.stats?.error) throw new Error(`Envoi interrompu (${sent.stats.error}). La campagne reste approuvée.`); }, 'Campagne envoyée.')}
            onReview={relaunchReview}
            onClose={() => { setTab('overview'); setTimeout(() => document.getElementById('campaign-comment')?.focus(), 50); }}
          />
        </div>

        {/* Onglets */}
        <nav className="mt-2 flex gap-5 border-b border-gray-200" aria-label="Sections">
          {([
            ['overview', 'Vue d’ensemble', comments],
            ['content', 'Contenu', null],
            ['preview', 'Aperçu', null]
          ] as [Tab, string, number | null][]).map(([id, label, count]) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`-mb-px flex items-center gap-1.5 border-b-2 py-2.5 text-[13.5px] font-medium ${tab === id ? 'border-indigo-600 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-800'}`}
            >
              {label}
              {count !== null && count > 0 && <span className="rounded-full bg-gray-200/70 px-1.5 text-[11px] font-semibold text-gray-600">{count}</span>}
            </button>
          ))}
        </nav>
      </header>

      <Toast notice={notice} onDone={() => setNotice(null)} />

      <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_288px]">
        <div className="min-w-0">
          {tab === 'overview' && (
            <>
              {campaign.note && (
                <div className={`${box} mb-4 px-4 py-3`}>
                  <p className="mb-1 text-[11.5px] font-semibold uppercase tracking-wide text-gray-400">Description</p>
                  <p className="whitespace-pre-wrap text-[13.5px] text-gray-800">{campaign.note}</p>
                </div>
              )}

              <ChecksCard
                campaign={campaign}
                isAuthor={isAuthor}
                target={target}
                channels={channels}
                reviewing={reviewing}
                busy={busy}
                onReview={relaunchReview}
                onOpenContent={() => setTab('content')}
              />

              {/* Fil d'activité */}
              <div className="mb-3 mt-7 flex flex-wrap items-center justify-between gap-2">
                <h3 className="text-[14px] font-semibold text-gray-900">Activité</h3>
                <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5 text-[12.5px] font-medium">
                  {([['all', 'Tout'], ['comments', 'Commentaires'], ['history', 'Historique']] as [Filter, string][]).map(([id, label]) => (
                    <button key={id} type="button" onClick={() => setFilter(id)} className={`rounded-md px-2.5 py-1 ${filter === id ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{label}</button>
                  ))}
                </div>
              </div>
              <CommentActionsContext.Provider value={commentActions}>
                <Timeline
                  events={events.filter((e) => filter === 'all' || (filter === 'comments' ? ['comment', 'ai_review'].includes(e.kind) : !['comment', 'ai_review'].includes(e.kind)))}
                  campaign={campaign}
                  staff={staff}
                />
              </CommentActionsContext.Provider>
              <span ref={timelineEndRef} />

              <CommentBox
                staff={staff}
                disabled={busy}
                canClose={open}
                onComment={async (body) => { await act(async () => { await commentCampaign(token, campaign.id_campaign, body); }); setFilter('all'); setTimeout(() => timelineEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 80); }}
                onClose={(reason) => act(() => rejectCampaign(token, campaign.id_campaign, reason || undefined), 'Campagne fermée. Elle ne partira pas.')}
              />
            </>
          )}

          {tab === 'content' && (
            <ContentEditor
              campaign={campaign}
              token={token}
              onSaved={async (msg) => { await load(); setNotice({ tone: 'ok', text: msg }); }}
              onError={(msg) => setNotice({ tone: 'error', text: msg })}
            />
          )}

          {tab === 'preview' && <PreviewPane campaign={campaign} token={token} />}
        </div>

        <div className="lg:sticky lg:self-start lg:pb-4" style={{ top: headerH + 16 }}>
          <Sidebar campaign={campaign} token={token} staff={staff} busy={busy} onAct={act} />
        </div>
      </div>
    </div>
  );
};

// --- En-tête : actions -------------------------------------------------------------------

/** Petit menu déroulant fermé par un clic dehors ou Échap. */
const useDismiss = (open: boolean, close: () => void) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (ev: MouseEvent) => { if (ref.current && !ref.current.contains(ev.target as Node)) close(); };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open, close]);
  return ref;
};

const HeaderActions = ({ campaign, busy, reviewing, canApprove, isAuthor, target, channels, onApprove, onRevoke, onSend, onReview, onClose }: {
  campaign: MarketingCampaign; busy: boolean; reviewing: boolean; canApprove: boolean; isAuthor: boolean; target: string; channels: string;
  onApprove: () => void; onRevoke: () => void; onSend: () => void; onReview: () => void; onClose: () => void;
}) => {
  const [menu, setMenu] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const closeMenu = useCallback(() => setMenu(false), []);
  const closeConfirm = useCallback(() => setConfirmSend(false), []);
  const menuRef = useDismiss(menu, closeMenu);
  const confirmRef = useDismiss(confirmSend, closeConfirm);
  const open = ['pending', 'approved'].includes(campaign.status);
  if (!open) return null;

  const items: { label: string; onClick: () => void; danger?: boolean; hidden?: boolean }[] = [
    { label: reviewing ? 'Relecture IA en cours…' : 'Relancer la relecture IA', onClick: onReview, hidden: !campaign.ai_reviewer },
    { label: 'Retirer l’approbation', onClick: onRevoke, hidden: campaign.status !== 'approved' },
    { label: 'Copier le lien', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => undefined); } },
    { label: 'Fermer sans envoyer', onClick: onClose, danger: true }
  ];

  return (
    <div className="flex shrink-0 items-center gap-2">
      {campaign.status === 'pending' && (
        <button
          type="button"
          disabled={busy || !canApprove}
          title={isAuthor ? 'Vous avez écrit cette campagne. Une autre personne doit l’approuver.' : (campaign.channels || []).length === 0 ? 'Choisissez au moins un canal.' : undefined}
          onClick={onApprove}
          className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-[13.5px] font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-45"
        >
          <Ico d="M5 12.5l4.5 4.5L19 7.5" /> Approuver
        </button>
      )}
      {campaign.status === 'approved' && (
        <div className="relative" ref={confirmRef}>
          <button type="button" disabled={busy} onClick={() => setConfirmSend((v) => !v)} className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-3.5 py-2 text-[13.5px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            <Ico d="M4 12l16-8-6 16-2.5-6.5L4 12z" /> Envoyer
          </button>
          {confirmSend && (
            <div className="absolute right-0 top-11 z-30 w-80 rounded-2xl border border-gray-100 bg-white p-4 shadow-[0_12px_32px_rgb(15_23_42/0.18)]">
              <p className="text-[13.5px] font-semibold text-gray-900">Envoyer maintenant ?</p>
              <p className="mt-1 text-[13px] text-gray-600">La campagne part vers {target.toLowerCase()} ({channels}). Un envoi ne se rattrape pas.</p>
              <div className="mt-3 flex justify-end gap-2">
                <button type="button" onClick={() => setConfirmSend(false)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-50">Annuler</button>
                <button type="button" disabled={busy} onClick={() => { setConfirmSend(false); onSend(); }} className="rounded-lg bg-red-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-red-700 disabled:opacity-50">Confirmer l’envoi</button>
              </div>
            </div>
          )}
        </div>
      )}
      <div className="relative" ref={menuRef}>
        <button type="button" aria-label="Plus d’actions" onClick={() => setMenu((v) => !v)} className={`flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 ${menu ? 'ring-2 ring-indigo-100' : ''}`}>
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
        </button>
        {menu && (
          <div role="menu" className="absolute right-0 top-11 z-30 w-60 rounded-2xl border border-gray-100 bg-white p-1.5 shadow-[0_12px_32px_rgb(15_23_42/0.18)]">
            {items.filter((i) => !i.hidden).map((i) => (
              <button key={i.label} role="menuitem" type="button" disabled={busy && !i.danger} onClick={() => { setMenu(false); i.onClick(); }} className={`w-full rounded-xl px-3 py-2 text-left text-[13.5px] disabled:opacity-50 ${i.danger ? 'text-red-600 hover:bg-red-50' : 'text-gray-800 hover:bg-gray-50'}`}>
                {i.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/** Message passager en bas de l'écran, au lieu d'une bannière qui pousse la page. */
const Toast = ({ notice, onDone }: { notice: Notice; onDone: () => void }) => {
  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(onDone, notice.tone === 'error' ? 8000 : 3500);
    return () => clearTimeout(t);
  }, [notice, onDone]);
  if (!notice) return null;
  return (
    <div role="status" className={`fixed bottom-6 left-1/2 z-50 flex max-w-[min(92vw,560px)] -translate-x-1/2 items-start gap-3 rounded-2xl px-4 py-3 text-[13.5px] shadow-[0_12px_32px_rgb(15_23_42/0.22)] ${notice.tone === 'error' ? 'bg-red-600 text-white' : notice.tone === 'warn' ? 'bg-amber-500 text-white' : 'bg-gray-900 text-white'}`}>
      <span className="min-w-0 flex-1">{notice.text}</span>
      <button type="button" onClick={onDone} aria-label="Fermer" className="-mr-1 shrink-0 opacity-70 hover:opacity-100"><Ico d="M6 6l12 12M18 6L6 18" /></button>
    </div>
  );
};

// --- Vérifications (façon stratégies de branche) ------------------------------------------

/** Erreur de l'IA dite simplement ; le détail technique reste accessible. */
export const aiErrorText = (message?: string | null) => {
  const m = String(message || '');
  if (/429|rate.?limit/i.test(m)) return 'Le service d’IA reçoit trop de demandes en ce moment. Réessayez dans une minute.';
  if (/tier_not_allowed|not available in your subscription/i.test(m)) return 'Ce modèle d’IA n’est pas inclus dans l’abonnement du fournisseur.';
  if (/\b401\b|unauthori|invalid.*key|api key not valid/i.test(m)) return 'La clé du fournisseur d’IA est refusée.';
  if (/temps|timeout|abort/i.test(m)) return 'Le service d’IA n’a pas répondu à temps.';
  return 'La relecture n’a pas abouti.';
};

const CheckRow = ({ state, title, detail, action }: { state: 'ok' | 'wait' | 'warn' | 'fail' | 'off'; title: React.ReactNode; detail?: React.ReactNode; action?: React.ReactNode }) => {
  const icon = {
    ok: <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-100 text-green-700"><Ico d="M5 12.5l4.5 4.5L19 7.5" /></span>,
    wait: <span className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-dashed border-gray-300" />,
    warn: <span className="flex h-6 w-6 items-center justify-center rounded-full bg-amber-100 text-[13px] font-bold text-amber-700">!</span>,
    fail: <span className="flex h-6 w-6 items-center justify-center rounded-full bg-red-100 text-red-700"><Ico d="M6 6l12 12M18 6L6 18" /></span>,
    off: <span className="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100 text-gray-400"><Ico d="M5 12h14" /></span>
  }[state];
  return (
    <li className="flex items-start gap-3 px-4 py-3">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1 text-[13.5px]">
        <p className="text-gray-900">{title}</p>
        {detail && <div className="mt-0.5 text-[12.5px] text-gray-500">{detail}</div>}
      </div>
      {action && <div className="shrink-0">{action}</div>}
    </li>
  );
};

const ChecksCard = ({ campaign, isAuthor, target, channels, reviewing, busy, onReview, onOpenContent }: {
  campaign: MarketingCampaign; isAuthor: boolean; target: string; channels: string; reviewing: boolean; busy: boolean; onReview: () => void; onOpenContent: () => void;
}) => {
  const [details, setDetails] = useState(false);
  const open = ['pending', 'approved'].includes(campaign.status);
  const approved = Boolean(campaign.approved_at && campaign.status !== 'pending' && campaign.status !== 'rejected');
  const review = campaign.ai_review;
  const current = Boolean(review && campaign.ai_review_current);
  const issues = review?.languages ? Object.values(review.languages).reduce((n: number, l: any) => n + (l?.issues?.length || 0), 0) : 0;
  const link = 'text-[12.5px] font-medium text-indigo-600 hover:underline disabled:opacity-50';

  const aiRow = !campaign.ai_reviewer ? null : reviewing || !current || !review ? (
    <CheckRow state="wait" title={<><strong className="font-semibold">Relecture IA</strong> en cours</>} detail="Orthographe et formulation en français, anglais et arabe. Cela peut prendre jusqu’à 30 secondes." />
  ) : review.status === 'ok' ? (
    <CheckRow state="ok" title={<><strong className="font-semibold">Relecture IA</strong> sans remarque</>} detail={review.model} />
  ) : review.status === 'issues' ? (
    <CheckRow state="warn" title={<><strong className="font-semibold">Relecture IA</strong> {issues} {issues > 1 ? 'points à corriger' : 'point à corriger'}</>} detail="Avis consultatif, il ne bloque pas l’approbation." action={open && <button type="button" className={link} onClick={onOpenContent}>Corriger</button>} />
  ) : review.status === 'unavailable' ? (
    <CheckRow state="off" title={<><strong className="font-semibold">Relecture IA</strong> non configurée</>} detail="Ajoutez une clé d’IA à l’API dans Coolify (MISTRAL_API_KEY ou GEMINI_API_KEY)." />
  ) : (
    <CheckRow
      state="fail"
      title={<><strong className="font-semibold">Relecture IA</strong> impossible</>}
      detail={<>
        {aiErrorText(review.message)}{' '}
        <button type="button" onClick={() => setDetails((v) => !v)} className="font-medium text-gray-600 underline decoration-gray-300 hover:text-gray-900">{details ? 'Masquer le détail' : 'Voir le détail'}</button>
        {details && <code className="mt-1.5 block whitespace-pre-wrap break-all rounded-lg bg-gray-50 px-2 py-1.5 font-mono text-[11.5px] text-gray-600">{review.message}</code>}
      </>}
      action={open && <button type="button" disabled={busy} className={link} onClick={onReview}>Relancer</button>}
    />
  );

  return (
    <section className={`${box} overflow-hidden`}>
      <p className="border-b border-gray-100 px-4 py-2.5 text-[12px] font-semibold uppercase tracking-wide text-gray-400">Avant l’envoi</p>
      <ul className="divide-y divide-gray-100">
        {approved ? (
          <CheckRow state="ok" title={<span className="flex flex-wrap items-center gap-1.5"><strong className="font-semibold">Approuvée</strong> par <Avatar name={campaign.approved_by_name} size={18} /> {campaign.approved_by_name || 'un administrateur'}</span>} detail={ago(campaign.approved_at)} />
        ) : campaign.status === 'rejected' ? (
          <CheckRow state="off" title={<strong className="font-semibold">Fermée sans approbation</strong>} />
        ) : (
          <CheckRow state="wait" title={<><strong className="font-semibold">1 approbation requise</strong>, par une autre personne que {campaign.created_by ? 'l’auteur' : 'le demandeur'}</>} detail={isAuthor ? 'Vous avez écrit cette campagne. Demandez une relecture dans la colonne de droite.' : undefined} />
        )}
        {aiRow}
        {campaign.status === 'sent' ? (
          <CheckRow state="ok" title={<><strong className="font-semibold">Envoyée</strong> {formatDateTime(campaign.sent_at)}</>} detail={`${campaign.stats?.recipients ?? 0} comptes visés · ${campaign.stats?.push?.sent ?? 0} push · ${campaign.stats?.email?.sent ?? 0} e-mails${(campaign.stats?.push?.failed || campaign.stats?.email?.failed) ? ` · ${(campaign.stats?.push?.failed || 0) + (campaign.stats?.email?.failed || 0)} échec(s)` : ''}`} />
        ) : campaign.status === 'sending' ? (
          <CheckRow state="wait" title={<strong className="font-semibold">Envoi en cours…</strong>} />
        ) : campaign.status === 'rejected' ? (
          <CheckRow state="off" title={<><strong className="font-semibold">Fermée</strong> sans envoi{campaign.reviewed_at ? ` · ${formatDateTime(campaign.reviewed_at)}` : ''}</>} />
        ) : campaign.status === 'approved' ? (
          <CheckRow state="ok" title={<><strong className="font-semibold">Prête à partir</strong> vers {target.toLowerCase()} ({channels})</>} detail={campaign.stats?.error ? <span className="text-amber-700">Envoi précédent interrompu ({campaign.stats.error})</span> : 'Le bouton Envoyer est en haut de la page.'} />
        ) : (
          <CheckRow state="off" title={<><strong className="font-semibold">Envoi bloqué</strong> jusqu’à l’approbation</>} />
        )}
      </ul>
    </section>
  );
};

// --- Fil d'activité -------------------------------------------------------------------------

const Timeline = ({ events, campaign, staff }: { events: CampaignEvent[]; campaign: MarketingCampaign; staff: CampaignPerson[] }) => {
  if (events.length === 0) return <p className="rounded-xl border border-dashed border-gray-200 px-4 py-6 text-center text-[13px] text-gray-500">Rien à afficher ici pour l’instant.</p>;
  return (
    <ol className="relative space-y-3 before:absolute before:bottom-2 before:left-[15px] before:top-2 before:w-px before:bg-gray-200">
      {events.map((e) => <TimelineItem key={e.id_event} event={e} campaign={campaign} staff={staff} />)}
    </ol>
  );
};

/** Texte d'un commentaire, avec les @mentions de l'équipe mises en évidence. */
const CommentText = ({ body, staff }: { body: string; staff: CampaignPerson[] }) => {
  const names = staff.map((p) => p.name).filter(Boolean).sort((a, b) => String(b).length - String(a).length) as string[];
  if (names.length === 0) return <>{body}</>;
  const escape = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = body.split(new RegExp(`(@(?:${names.map(escape).join('|')}))`, 'g'));
  return <>{parts.map((part, i) => (part.startsWith('@') && names.includes(part.slice(1)) ? <span key={i} className="rounded bg-indigo-50 px-1 font-medium text-indigo-700">{part}</span> : <React.Fragment key={i}>{part}</React.Fragment>))}</>;
};

const Arrow = () => <svg className="mx-1 inline h-3.5 w-3.5 align-[-2px] text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>;

const SystemNote = ({ icon, children, at, tone = 'text-gray-500' }: { icon: React.ReactNode; children: React.ReactNode; at: string; tone?: string }) => (
  <li className="relative flex items-start gap-3 pl-0">
    <span className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-50 ring-4 ring-gray-50 ${tone}`}>{icon}</span>
    <div className="min-w-0 flex-1 pt-1.5 text-[13px] text-gray-600">
      {children} <span className="text-gray-400">· {ago(at)}</span>
    </div>
  </li>
);

const Ico = ({ d }: { d: string }) => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24" aria-hidden="true"><path d={d} /></svg>
);

const TimelineItem = ({ event: e, campaign, staff }: { event: CampaignEvent; campaign: MarketingCampaign; staff: CampaignPerson[] }) => {
  const [open, setOpen] = useState(false);
  const who = <strong className="font-semibold text-gray-900">{e.actor_name || (e.actor_id ? 'Un administrateur' : 'L’API Dingo')}</strong>;
  switch (e.kind) {
    case 'created':
      return <SystemNote icon={<Ico d="M12 5v14M5 12h14" />} at={e.created_at}>{e.actor_id ? <>{who} a ouvert la campagne</> : <><strong className="font-semibold text-gray-900">L’API Dingo</strong> a proposé la campagne{e.body ? `. ${e.body}` : ''}</>}</SystemNote>;
    case 'reviewers_changed': {
      const d = e.data || {};
      const parts: string[] = [];
      const self = (d.added || []).filter((n: string) => n === e.actor_name);
      const others = (d.added || []).filter((n: string) => n !== e.actor_name);
      if (self.length) parts.push('s’est assigné la relecture');
      if (others.length) parts.push(`a demandé une relecture à ${others.join(', ')}`);
      if (d.removed?.length) parts.push(`a retiré ${d.removed.join(', ')} des relecteurs`);
      if (d.ai === true) parts.push('a assigné l’IA comme relectrice');
      if (d.ai === false) parts.push('a retiré l’IA des relecteurs');
      return <SystemNote icon={<Ico d="M16 11a4 4 0 1 0-8 0M4 20c1.2-3 4.2-5 8-5s6.8 2 8 5" />} at={e.created_at}>{who} {parts.join(' et ') || 'a modifié les relecteurs'}</SystemNote>;
    }
    case 'approved':
      return <SystemNote icon={<Ico d="M5 12.5l4.5 4.5L19 7.5" />} tone="text-green-700" at={e.created_at}>{who} <span className="font-medium text-green-700">a approuvé</span> la campagne</SystemNote>;
    case 'approval_revoked':
      return <SystemNote icon={<Ico d="M6 6l12 12M18 6L6 18" />} at={e.created_at}>{who} a retiré son approbation</SystemNote>;
    case 'approval_reset':
      return <SystemNote icon={<Ico d="M4 12a8 8 0 1 0 2.3-5.7M4 4v4h4" />} tone="text-amber-700" at={e.created_at}><span className="text-amber-800">Approbation retirée : la campagne a été modifiée après approbation.</span></SystemNote>;
    case 'sent': {
      const s = e.data || {};
      return <SystemNote icon={<Ico d="M4 12l16-8-6 16-2.5-6.5L4 12z" />} tone="text-violet-700" at={e.created_at}>{who} <span className="font-medium text-violet-700">a envoyé</span> la campagne · {s.recipients ?? 0} comptes, {s.push?.sent ?? 0} push, {s.email?.sent ?? 0} e-mails</SystemNote>;
    }
    case 'send_failed':
      return <SystemNote icon={<Ico d="M12 8v5M12 16.5v.5M10.3 4.3L2.8 17.5A2 2 0 0 0 4.5 20.5h15a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0z" />} tone="text-red-700" at={e.created_at}><span className="text-red-700">Envoi interrompu{e.body ? ` (${e.body})` : ''}.</span> La campagne reste approuvée.</SystemNote>;
    case 'rejected':
      return <SystemNote icon={<Ico d="M6 6l12 12M18 6L6 18" />} tone="text-red-700" at={e.created_at}>{who} a <span className="font-medium text-red-700">fermé</span> la campagne{e.body ? <> : « {e.body} »</> : ''}</SystemNote>;
    case 'edited': {
      const changes: any[] = e.data?.changes || [];
      const settings: any[] = e.data?.settings || [];
      const total = changes.length + settings.length;
      return (
        <SystemNote icon={<Ico d="M4 20h4L19 9l-4-4L4 16v4zM14 6l4 4" />} at={e.created_at}>
          {who} a modifié la campagne{' '}
          <button type="button" onClick={() => setOpen((v) => !v)} className="font-medium text-indigo-600 hover:underline">
            {open ? 'masquer' : `voir les ${total} modification${total > 1 ? 's' : ''}`}
          </button>
          {open && (
            <div className="mt-2 space-y-2">
              {changes.map((c, i) => (
                <div key={i} className="overflow-hidden rounded-lg border border-gray-200 bg-white text-[12.5px]">
                  <p className="border-b border-gray-100 bg-gray-50 px-3 py-1.5 font-medium text-gray-700">
                    <span className="uppercase">{c.lang}</span> · {FIELD_LABEL[c.field] || c.field}
                  </p>
                  <p dir={c.lang === 'ar' ? 'rtl' : 'ltr'} className="whitespace-pre-wrap bg-red-50 px-3 py-1.5 text-red-900"><span className="select-none text-red-400">− </span>{(c.field === 'html' ? stripHtml(c.before) : c.before) || <em className="text-red-400">(vide)</em>}</p>
                  <p dir={c.lang === 'ar' ? 'rtl' : 'ltr'} className="whitespace-pre-wrap bg-green-50 px-3 py-1.5 text-green-900"><span className="select-none text-green-500">+ </span>{(c.field === 'html' ? stripHtml(c.after) : c.after) || <em className="text-green-500">(vide)</em>}</p>
                </div>
              ))}
              {settings.map((c, i) => (
                <p key={`s${i}`} className="rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-[12.5px] text-gray-700">
                  {FIELD_LABEL[c.field] || c.field} : <span className="text-red-700 line-through">{Array.isArray(c.before) ? c.before.join(' + ') : (c.field === 'audience' ? AUDIENCE_LABEL[c.before as CampaignAudience] : c.before) || '-'}</span>
                  <Arrow /><span className="text-green-700">{Array.isArray(c.after) ? c.after.join(' + ') : (c.field === 'audience' ? AUDIENCE_LABEL[c.after as CampaignAudience] : c.after) || '-'}</span>
                </p>
              ))}
            </div>
          )}
        </SystemNote>
      );
    }
    case 'ai_review':
      return <AiReviewNote event={e} />;
    case 'comment':
      return <CommentItem event={e} campaign={campaign} staff={staff} />;
    default:
      return null;
  }
};

/** Relecture de l'IA, affichée comme le commentaire d'un relecteur. */
const AiReviewNote = ({ event: e }: { event: CampaignEvent }) => {
  const r = e.data || {};
  const langs = Object.keys(r.languages || {}) as CampaignLang[];
  const checked = langs.filter((l) => !r.languages[l]?.fallback);
  const fallbacks = langs.filter((l) => r.languages[l]?.fallback);
  const issues = langs.flatMap((l) => (r.languages[l]?.issues || []).map((i: any) => ({ lang: l, ...i })));
  return (
    <li className="relative flex items-start gap-3">
      <span className="relative z-10 rounded-full ring-4 ring-gray-50"><Avatar ai size={32} /></span>
      <div className={`min-w-0 flex-1 rounded-xl border ${r.status === 'issues' ? 'border-amber-200' : r.status === 'error' ? 'border-red-200' : 'border-green-200'} bg-white`}>
        <p className="border-b border-gray-100 px-3 py-2 text-[12.5px] text-gray-500">
          <strong className="font-semibold text-gray-900">IA Dingo</strong> a relu l’orthographe et la formulation · {ago(e.created_at)}
          {r.model && <span className="ml-1 text-gray-400">({r.model})</span>}
        </p>
        <div className="px-3 py-2.5 text-[13.5px]">
          {r.status === 'error' ? (
            <AiErrorText message={r.message} />
          ) : issues.length === 0 ? (
            <p className="text-green-800"><strong className="font-semibold">Rien à corriger</strong> en {checked.map((l) => LANG_NAME[l]).join(', ')}.{fallbacks.length > 0 && <span className="text-gray-500"> ({fallbacks.map((l) => LANG_NAME[l]).join(', ')} : reprend le français)</span>}</p>
          ) : (
            <>
              <p className="font-semibold text-amber-900">{issues.length} {issues.length > 1 ? 'points à corriger' : 'point à corriger'}</p>
              <ul className="mt-1.5 space-y-2">
                {issues.map((i, n) => (
                  <li key={n} className="text-[13px]">
                    <span className="font-semibold uppercase text-gray-700">{i.lang}</span> · <span className="text-gray-600">{FIELD_LABEL[i.field] || i.field}</span>
                    <span className="mt-0.5 block">
                      <span dir={i.lang === 'ar' ? 'rtl' : 'ltr'} className="rounded bg-red-50 px-1 text-red-900 line-through decoration-red-400">{i.excerpt}</span>
                      {i.suggestion && <><Arrow /><span dir={i.lang === 'ar' ? 'rtl' : 'ltr'} className="rounded bg-green-50 px-1 font-medium text-green-900">{i.suggestion}</span></>}
                    </span>
                    {i.problem && <span className="block text-[12px] text-gray-500">{i.problem}</span>}
                  </li>
                ))}
              </ul>
            </>
          )}
        </div>
      </div>
    </li>
  );
};

const AiErrorText = ({ message }: { message?: string }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <p className="text-red-700">{aiErrorText(message)}</p>
      {message && <button type="button" onClick={() => setOpen((v) => !v)} className="mt-1 text-[12px] font-medium text-gray-500 hover:text-gray-800">{open ? 'Masquer le détail' : 'Voir le détail'}</button>}
      {open && <code className="mt-1.5 block whitespace-pre-wrap break-all rounded-lg bg-gray-50 px-2 py-1.5 font-mono text-[11.5px] text-gray-600">{message}</code>}
    </div>
  );
};

const norm = (v?: string | null) => String(v || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
const matches = (p: CampaignPerson, query: string) => !query || norm(p.name).includes(norm(query)) || norm(p.email).includes(norm(query));

/** Liste de suggestions au clavier : flèches, Entrée ou Tab pour choisir, Échap pour fermer. */
const SuggestionList = ({ people, active, onPick, onHover, ai }: { people: CampaignPerson[]; active: number; onPick: (p: CampaignPerson) => void; onHover: (i: number) => void; ai?: React.ReactNode }) => (
  <ul role="listbox" className="max-h-60 overflow-y-auto overscroll-contain py-1">
    {ai}
    {people.map((p, i) => (
      <li key={p.id} role="option" aria-selected={i === active}>
        <button
          type="button"
          onMouseDown={(ev) => { ev.preventDefault(); onPick(p); }}
          onMouseEnter={() => onHover(i)}
          className={`flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left ${i === active ? 'bg-indigo-50' : ''}`}
        >
          <Avatar name={p.name} photo={p.photo} size={24} />
          <span className="min-w-0 flex-1">
            <span className="block truncate text-[13px] text-gray-900">{p.name || p.email}</span>
            {p.name && <span className="block truncate text-[11.5px] text-gray-500">{p.email}</span>}
          </span>
        </button>
      </li>
    ))}
  </ul>
);

/**
 * Zone de saisie à @mentions : taper @ propose l'équipe (flèches, Entrée ou Tab
 * pour choisir, Échap pour fermer) ; Ctrl + Entrée valide. À placer dans un
 * parent `relative`, la liste s'ouvre au-dessus.
 */
const MentionArea = ({ value, onChange, onSubmit, onEscape, staff, id, placeholder, rows = 3, autoFocus = false, className = '' }: {
  value: string; onChange: (v: string) => void; onSubmit: () => void; onEscape?: () => void; staff: CampaignPerson[];
  id?: string; placeholder?: string; rows?: number; autoFocus?: boolean; className?: string;
}) => {
  const [mention, setMention] = useState<{ start: number; query: string } | null>(null);
  const [active, setActive] = useState(0);
  const area = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (!autoFocus || !area.current) return;
    area.current.focus();
    area.current.setSelectionRange(area.current.value.length, area.current.value.length);
  }, [autoFocus]);

  const suggestions = mention ? staff.filter((p) => p.name && matches(p, mention.query)).slice(0, 6) : [];

  // Une mention commence par @ en début de mot ; elle se referme à l'espace double ou au retour à la ligne.
  const detect = (text: string, caret: number) => {
    const m = /(^|\s)@([^\s@]{0,20}(?: [^\s@]{0,20})?)$/.exec(text.slice(0, caret));
    if (m) { setMention({ start: caret - m[2].length - 1, query: m[2] }); setActive(0); } else setMention(null);
  };

  const pick = (p: CampaignPerson) => {
    if (!mention || !area.current) return;
    const caret = area.current.selectionStart;
    const pos = mention.start + String(p.name).length + 2;
    onChange(`${value.slice(0, mention.start)}@${p.name} ${value.slice(caret)}`);
    setMention(null);
    requestAnimationFrame(() => { area.current?.focus(); area.current?.setSelectionRange(pos, pos); });
  };

  const onKeyDown = (ev: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && suggestions.length > 0) {
      if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive((i) => (i + 1) % suggestions.length); return; }
      if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (ev.key === 'Enter' || ev.key === 'Tab') { ev.preventDefault(); pick(suggestions[active]); return; }
      if (ev.key === 'Escape') { ev.preventDefault(); setMention(null); return; }
    }
    if (ev.key === 'Escape' && onEscape) { ev.preventDefault(); onEscape(); return; }
    if (ev.key === 'Enter' && (ev.metaKey || ev.ctrlKey)) { ev.preventDefault(); setMention(null); onSubmit(); }
  };

  return (
    <>
      <textarea
        id={id}
        ref={area}
        value={value}
        onChange={(e) => { onChange(e.target.value); detect(e.target.value, e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onClick={(e) => detect(value, (e.target as HTMLTextAreaElement).selectionStart)}
        onBlur={() => setTimeout(() => setMention(null), 120)}
        rows={rows}
        placeholder={placeholder}
        className={`block w-full resize-y border-0 bg-transparent px-3.5 py-3 text-[13.5px] focus:outline-none focus:ring-0 ${className}`}
      />
      {mention && suggestions.length > 0 && (
        <div className="absolute bottom-full left-3 z-30 mb-1 w-72 rounded-xl border border-gray-100 bg-white p-1 shadow-[0_12px_32px_rgb(15_23_42/0.18)]">
          <p className="px-2.5 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-gray-400">Mentionner</p>
          <SuggestionList people={suggestions} active={active} onPick={pick} onHover={setActive} />
        </div>
      )}
    </>
  );
};

const CommentBox = ({ staff, disabled, canClose, onComment, onClose }: { staff: CampaignPerson[]; disabled: boolean; canClose: boolean; onComment: (body: string) => Promise<void>; onClose: (reason: string) => Promise<void> }) => {
  const [body, setBody] = useState('');
  const [confirmClose, setConfirmClose] = useState(false);

  const submit = async () => {
    const text = body.trim();
    if (!text || disabled) return;
    setBody('');
    await onComment(text);
  };

  return (
    <div className="relative mt-5 rounded-2xl border border-gray-200 bg-white focus-within:border-indigo-300 focus-within:ring-4 focus-within:ring-indigo-50">
      <MentionArea
        id="campaign-comment"
        value={body}
        onChange={setBody}
        onSubmit={submit}
        staff={staff}
        placeholder="Écrire un commentaire. Tapez @ pour mentionner quelqu’un de l’équipe."
        className="rounded-t-2xl"
      />
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-3 py-2">
        {canClose ? (
          confirmClose ? (
            <span className="flex flex-wrap items-center gap-2 text-[12.5px] text-red-700">
              Fermer sans envoyer{body.trim() ? ', avec ce commentaire comme motif' : ''} ?
              <button type="button" onClick={() => setConfirmClose(false)} className="text-gray-600 hover:underline">Annuler</button>
              <button type="button" disabled={disabled} onClick={async () => { setConfirmClose(false); await onClose(body.trim()); setBody(''); }} className="font-medium underline">Fermer la campagne</button>
            </span>
          ) : (
            <button type="button" disabled={disabled} onClick={() => setConfirmClose(true)} className="text-[12.5px] font-medium text-red-600 hover:underline">Fermer la campagne</button>
          )
        ) : <span />}
        <span className="flex items-center gap-3">
          <span className="hidden text-[11.5px] text-gray-400 sm:inline">Ctrl + Entrée pour envoyer</span>
          <button type="button" disabled={disabled || !body.trim()} onClick={submit} className="rounded-xl bg-gray-900 px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-40">
            Commenter
          </button>
        </span>
      </div>
    </div>
  );
};

/** Ce qu'un commentaire peut faire de lui-même : seul son auteur le modifie ou le supprime. */
interface CommentActions {
  meId: string;
  busy: boolean;
  onEdit: (eventId: string, body: string) => Promise<boolean>;
  onDelete: (eventId: string) => Promise<void>;
}
const CommentActionsContext = React.createContext<CommentActions | null>(null);

const CommentItem = ({ event: e, campaign, staff }: { event: CampaignEvent; campaign: MarketingCampaign; staff: CampaignPerson[] }) => {
  const actions = React.useContext(CommentActionsContext);
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draft, setDraft] = useState(e.body || '');
  const closeMenu = useCallback(() => { setMenu(false); setConfirmDelete(false); }, []);
  const menuRef = useDismiss(menu, closeMenu);
  const mine = Boolean(actions && e.actor_id && String(e.actor_id) === actions.meId);
  const edited = e.data?.edited_at;

  const save = async () => {
    const text = draft.trim();
    if (!actions || !text) return;
    if (text === (e.body || '').trim()) { setEditing(false); return; }
    if (await actions.onEdit(e.id_event, text)) setEditing(false);
  };

  return (
    <li className="group relative flex items-start gap-3">
      <span className="relative z-10 rounded-full ring-4 ring-gray-50"><Avatar name={e.actor_name} photo={e.actor_photo} size={32} /></span>
      <div className={`min-w-0 flex-1 rounded-xl border bg-white ${editing ? 'border-indigo-300 ring-4 ring-indigo-50' : 'border-gray-200'}`}>
        <div className="flex items-center gap-2 border-b border-gray-100 px-3 py-2 text-[12.5px] text-gray-500">
          <p className="min-w-0 flex-1">
            <strong className="font-semibold text-gray-900">{e.actor_name || 'Un administrateur'}</strong> · {ago(e.created_at)}
            {edited && <span title={`Modifié ${formatDateTime(edited)}`} className="text-gray-400"> · modifié</span>}
            {campaign.created_by && String(campaign.created_by) === String(e.actor_id) && <Label tone="ml-2 bg-gray-100 text-gray-600">Auteur</Label>}
          </p>
          {mine && !editing && (
            <div className="relative" ref={menuRef}>
              <button type="button" aria-label="Actions du commentaire" onClick={() => setMenu((v) => !v)} className={`flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700 focus:opacity-100 ${menu ? 'bg-gray-100 text-gray-700 opacity-100' : 'opacity-60 group-hover:opacity-100'}`}>
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="5" cy="12" r="1.7" /><circle cx="12" cy="12" r="1.7" /><circle cx="19" cy="12" r="1.7" /></svg>
              </button>
              {menu && (
                <div role="menu" className="absolute right-0 top-8 z-30 w-56 rounded-2xl border border-gray-100 bg-white p-1.5 shadow-[0_12px_32px_rgb(15_23_42/0.18)]">
                  {confirmDelete ? (
                    <div className="px-2.5 py-2">
                      <p className="text-[13px] text-gray-800">Supprimer ce commentaire ? Il disparaît du fil pour tout le monde.</p>
                      <div className="mt-2.5 flex justify-end gap-1.5">
                        <button type="button" onClick={() => setConfirmDelete(false)} className="rounded-lg px-2.5 py-1 text-[12.5px] text-gray-600 hover:bg-gray-50">Annuler</button>
                        <button type="button" disabled={actions?.busy} onClick={async () => { closeMenu(); await actions?.onDelete(e.id_event); }} className="rounded-lg bg-red-600 px-2.5 py-1 text-[12.5px] font-medium text-white hover:bg-red-700 disabled:opacity-50">Supprimer</button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <button role="menuitem" type="button" onClick={() => { closeMenu(); setDraft(e.body || ''); setEditing(true); }} className="w-full rounded-xl px-3 py-2 text-left text-[13.5px] text-gray-800 hover:bg-gray-50">Modifier</button>
                      <button role="menuitem" type="button" onClick={() => setConfirmDelete(true)} className="w-full rounded-xl px-3 py-2 text-left text-[13.5px] text-red-600 hover:bg-red-50">Supprimer</button>
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        {editing ? (
          <div className="relative">
            <MentionArea value={draft} onChange={setDraft} onSubmit={save} onEscape={() => setEditing(false)} staff={staff} rows={3} autoFocus />
            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-3 py-2">
              <span className="mr-auto hidden text-[11.5px] text-gray-400 sm:inline">Ctrl + Entrée pour enregistrer, Échap pour annuler</span>
              <button type="button" onClick={() => setEditing(false)} className="rounded-lg px-3 py-1.5 text-[13px] text-gray-600 hover:bg-gray-50">Annuler</button>
              <button type="button" disabled={actions?.busy || !draft.trim()} onClick={save} className="rounded-xl bg-gray-900 px-3.5 py-1.5 text-[13px] font-medium text-white hover:bg-gray-800 disabled:opacity-40">Enregistrer</button>
            </div>
          </div>
        ) : (
          <p className="whitespace-pre-wrap px-3 py-2.5 text-[13.5px] text-gray-800"><CommentText body={e.body || ''} staff={staff} /></p>
        )}
      </div>
    </li>
  );
};

// --- Barre latérale ---------------------------------------------------------------------

/** Choix des relecteurs : recherche avec autocomplétion, comme le champ Reviewers d'Azure DevOps. */
const ReviewerPicker = ({ campaign, staff, busy, onSave, onCancel }: { campaign: MarketingCampaign; staff: CampaignPerson[]; busy: boolean; onSave: (ids: string[], ai: boolean) => void; onCancel: () => void }) => {
  const [selected, setSelected] = useState<string[]>((campaign.reviewer_ids || []).map(String));
  const [ai, setAi] = useState(campaign.ai_reviewer !== false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { input.current?.focus(); }, []);

  const pool = staff.filter((p) => String(p.id) !== String(campaign.created_by || ''));
  const chosen = selected.map((id) => pool.find((p) => String(p.id) === id) || (campaign.reviewers || []).find((r) => String(r.id) === id)).filter(Boolean) as CampaignPerson[];
  const results = pool.filter((p) => !selected.includes(String(p.id)) && matches(p, query)).slice(0, 8);
  const showAi = !ai && (!query || 'ia dingo'.includes(norm(query)));

  const add = (p: CampaignPerson) => { setSelected((cur) => [...cur, String(p.id)]); setQuery(''); setActive(0); input.current?.focus(); };
  const onKeyDown = (ev: React.KeyboardEvent<HTMLInputElement>) => {
    if (ev.key === 'ArrowDown') { ev.preventDefault(); setActive((i) => Math.min(i + 1, Math.max(results.length - 1, 0))); }
    else if (ev.key === 'ArrowUp') { ev.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
    else if (ev.key === 'Enter') { ev.preventDefault(); if (results[active]) add(results[active]); else if (!query) onSave(selected, ai); }
    else if (ev.key === 'Backspace' && !query && selected.length > 0) setSelected((cur) => cur.slice(0, -1));
    else if (ev.key === 'Escape') { ev.preventDefault(); onCancel(); }
  };

  return (
    <div className="absolute right-0 top-8 z-30 w-[300px] rounded-2xl border border-gray-100 bg-white p-2 shadow-[0_12px_32px_rgb(15_23_42/0.18)]">
      <div className="flex flex-wrap items-center gap-1.5 rounded-xl border border-gray-200 px-2 py-1.5 focus-within:border-indigo-300 focus-within:ring-4 focus-within:ring-indigo-50">
        {ai && (
          <span className="inline-flex items-center gap-1 rounded-full bg-indigo-50 py-0.5 pl-0.5 pr-1.5 text-[12px] text-indigo-800">
            <Avatar ai size={18} /> IA Dingo
            <button type="button" aria-label="Retirer l’IA" onClick={() => setAi(false)} className="text-indigo-400 hover:text-indigo-800">×</button>
          </span>
        )}
        {chosen.map((p) => (
          <span key={p.id} className="inline-flex max-w-full items-center gap-1 rounded-full bg-gray-100 py-0.5 pl-0.5 pr-1.5 text-[12px] text-gray-800">
            <Avatar name={p.name} photo={p.photo} size={18} /> <span className="truncate">{p.name || p.email}</span>
            <button type="button" aria-label={`Retirer ${p.name || p.email}`} onClick={() => setSelected((cur) => cur.filter((x) => x !== String(p.id)))} className="text-gray-400 hover:text-gray-800">×</button>
          </span>
        ))}
        <input
          ref={input}
          value={query}
          onChange={(e) => { setQuery(e.target.value); setActive(0); }}
          onKeyDown={onKeyDown}
          placeholder={selected.length || ai ? 'Ajouter…' : 'Chercher un nom ou un e-mail'}
          className="min-w-[90px] flex-1 border-0 p-0.5 text-[13px] focus:outline-none focus:ring-0"
        />
      </div>
      <div className="mt-1">
        {staff.length === 0 ? (
          <p className="px-2.5 py-2 text-[12.5px] text-gray-500">Chargement de l’équipe…</p>
        ) : results.length === 0 && !showAi ? (
          <p className="px-2.5 py-2 text-[12.5px] text-gray-500">{query ? `Personne ne correspond à « ${query} ».` : 'Toute l’équipe est déjà ajoutée.'}</p>
        ) : (
          <SuggestionList
            people={results}
            active={active}
            onPick={add}
            onHover={setActive}
            ai={showAi && (
              <li>
                <button type="button" onMouseDown={(ev) => { ev.preventDefault(); setAi(true); setQuery(''); }} className="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left hover:bg-gray-50">
                  <Avatar ai size={24} />
                  <span className="min-w-0 flex-1"><span className="block text-[13px] text-gray-900">IA Dingo</span><span className="block text-[11.5px] text-gray-500">orthographe et formulation, fr / en / ar</span></span>
                </button>
              </li>
            )}
          />
        )}
      </div>
      <div className="mt-1 flex items-center justify-between gap-2 border-t border-gray-100 px-1 pt-2">
        <span className="text-[11px] text-gray-400">Entrée pour ajouter</span>
        <span className="flex gap-1.5">
          <button type="button" onClick={onCancel} className="rounded-lg px-2.5 py-1 text-[12.5px] text-gray-600 hover:bg-gray-50">Annuler</button>
          <button type="button" disabled={busy} onClick={() => onSave(selected, ai)} className="rounded-lg bg-indigo-600 px-3 py-1 text-[12.5px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">Enregistrer</button>
        </span>
      </div>
    </div>
  );
};

const Sidebar = ({ campaign, token, staff, busy, onAct }: { campaign: MarketingCampaign; token: string; staff: CampaignPerson[]; busy: boolean; onAct: (fn: () => Promise<unknown>, ok?: string) => Promise<void> }) => {
  const [editing, setEditing] = useState(false);
  const stopEditing = useCallback(() => setEditing(false), []);
  const popRef = useDismiss(editing, stopEditing);
  const open = ['pending', 'approved'].includes(campaign.status);

  const review = campaign.ai_review;
  const aiState = !campaign.ai_reviewer
    ? { text: 'Non assignée', tone: 'text-gray-500' }
    : !review || !campaign.ai_review_current
      ? { text: 'En cours…', tone: 'text-gray-500' }
      : review.status === 'ok'
        ? { text: 'Rien à corriger', tone: 'text-green-700' }
        : review.status === 'issues'
          ? { text: `${Object.values(review.languages || {}).reduce((n, l: any) => n + (l?.issues?.length || 0), 0)} à corriger`, tone: 'text-amber-700' }
          : review.status === 'unavailable'
            ? { text: 'Non configurée', tone: 'text-gray-500' }
            : { text: 'Impossible', tone: 'text-red-700' };

  const row = 'border-b border-gray-200 py-3.5 first:pt-0';

  return (
    <aside className="text-[13px] lg:border-l lg:border-gray-200 lg:pl-5">
      {/* Relecteurs */}
      <div className={`${row} relative`} ref={popRef}>
        <div className="flex items-center justify-between">
          <p className={sideTitle}>Relecteurs</p>
          {open && <button type="button" onClick={() => setEditing((v) => !v)} className="text-[12px] font-medium text-indigo-600 hover:underline">{editing ? 'Fermer' : 'Modifier'}</button>}
        </div>
        {!(campaign.reviewers?.length) && !campaign.ai_reviewer ? (
          <p className="mt-1.5 text-gray-500">{open ? 'Aucun. Tout administrateur peut approuver.' : 'Aucun.'}</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {campaign.ai_reviewer && (
              <li className="flex items-center gap-2"><Avatar ai size={24} /><span className="flex-1 text-gray-800">IA Dingo</span><span className={`text-[11.5px] ${aiState.tone}`}>{aiState.text}</span></li>
            )}
            {(campaign.reviewers || []).map((r) => {
              const voted = String(campaign.approved_by || '') === String(r.id) && campaign.status !== 'pending';
              return (
                <li key={r.id} className="flex items-center gap-2">
                  <span className="relative">
                    <Avatar name={r.name} photo={r.photo} size={24} />
                    {voted && <span className="absolute -bottom-0.5 -right-0.5 flex h-3 w-3 items-center justify-center rounded-full bg-green-600 ring-2 ring-[#F7F7FB]"><svg className="h-2 w-2 text-white" fill="none" stroke="currentColor" strokeWidth="4" viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg></span>}
                  </span>
                  <span className="flex-1 truncate text-gray-800">{r.name || 'Compte'}</span>
                  <span className={`text-[11.5px] ${voted ? 'text-green-700' : 'text-gray-400'}`}>{voted ? 'A approuvé' : 'En attente'}</span>
                </li>
              );
            })}
          </ul>
        )}
        {editing && (
          <ReviewerPicker
            campaign={campaign}
            staff={staff}
            busy={busy}
            onCancel={stopEditing}
            onSave={async (ids, ai) => { setEditing(false); await onAct(() => setCampaignReviewers(token, campaign.id_campaign, ids, ai), 'Relecteurs mis à jour.'); }}
          />
        )}
      </div>

      {/* Étiquettes et réglages */}
      <div className={row}>
        <p className={sideTitle}>Étiquettes</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <Label tone={KIND_TONE[campaign.kind] || KIND_TONE.custom}>{KIND_LABEL[campaign.kind] || campaign.kind}</Label>
          {(campaign.channels || []).map((c) => <Label key={c}>{CHANNEL_LABEL[c] || c}</Label>)}
        </div>
      </div>
      <div className={row}>
        <p className={sideTitle}>Audience</p>
        <p className="mt-1 text-gray-600">{campaign.audience === 'user' && campaign.target_user_name ? `${campaign.target_user_name}${campaign.target_user_email ? ` · ${campaign.target_user_email}` : ''}` : AUDIENCE_LABEL[campaign.audience]}</p>
      </div>
      <div className={row}>
        <p className={sideTitle}>Lien ouvert au tap</p>
        <p className="mt-1 break-all font-mono text-[12px] text-gray-600">{campaign.link || '-'}</p>
      </div>
      <div className="py-3.5 text-[12px] text-gray-500">
        <p>Ouverte {formatDateTime(campaign.created_at)}</p>
        <p>Mise à jour {ago(campaign.updated_at)}</p>
        <p className="mt-1 font-mono">{ref(campaign)}</p>
      </div>
    </aside>
  );
};

// --- Contenu (édition) -------------------------------------------------------------------

const ContentEditor = ({ campaign, token, onSaved, onError }: { campaign: MarketingCampaign; token: string; onSaved: (msg: string) => Promise<void>; onError: (msg: string) => void }) => {
  const editable = ['pending', 'approved'].includes(campaign.status);
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [content, setContent] = useState<Record<CampaignLang, CampaignText>>(() => ({ ...emptyContent(), ...campaign.content }));
  const [channels, setChannels] = useState<CampaignChannel[]>(campaign.channels || []);
  const [audience, setAudience] = useState<CampaignAudience>(campaign.audience);
  const [link, setLink] = useState(campaign.link || '');
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setContent({ ...emptyContent(), ...campaign.content });
    setChannels(campaign.channels || []);
    setAudience(campaign.audience);
    setLink(campaign.link || '');
    setDirty(false);
  }, [campaign.id_campaign, campaign.updated_at]); // eslint-disable-line react-hooks/exhaustive-deps

  const text = content[lang] || emptyText();
  const dir = LANGS.find((l) => l.code === lang)?.dir || 'ltr';
  const setField = (field: keyof CampaignText, value: string) => { setContent((cur) => ({ ...cur, [lang]: { ...(cur[lang] || emptyText()), [field]: value } })); setDirty(true); };
  const emptyLang = lang !== 'fr' && !(text.title || '').trim();

  const save = async () => {
    setSaving(true);
    try {
      const wasApproved = campaign.status === 'approved';
      const updated = await updateCampaign(token, campaign.id_campaign, { content, channels, audience, link: link.trim() || null });
      await onSaved(wasApproved && updated.status === 'pending' ? 'Modifications enregistrées : l’approbation est retirée, une nouvelle approbation est nécessaire.' : 'Modifications enregistrées.');
    } catch (err: any) {
      onError(err.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {!editable && <p className="mb-3 rounded-xl bg-gray-100 px-3 py-2 text-[12.5px] text-gray-600">Campagne {campaign.status === 'sent' ? 'envoyée' : 'fermée'} : lecture seule.</p>}
      {editable && campaign.status === 'approved' && (
        <p className={`mb-3 rounded-xl px-3 py-2 text-[12.5px] ${dirty ? 'bg-amber-50 text-amber-900' : 'bg-indigo-50 text-indigo-800'}`}>
          {dirty ? 'Enregistrer ces modifications retirera l’approbation.' : 'Campagne approuvée : la modifier retirera l’approbation.'}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5 text-[13px] font-medium">
          {LANGS.map((l) => (
            <button key={l.code} type="button" onClick={() => setLang(l.code)} className={`rounded-lg px-3.5 py-1.5 transition-colors ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>
              {l.label}{l.code !== 'fr' && !(content[l.code]?.title || '').trim() && <span className="ml-1 text-[10px] opacity-60">(fr)</span>}
            </button>
          ))}
        </div>
        {editable && (
          <button type="button" disabled={!dirty || saving} onClick={save} className="rounded-xl bg-indigo-600 px-3.5 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        )}
      </div>
      {emptyLang && (
        <p className="mt-3 rounded-xl bg-gray-50 px-3 py-2 text-[12.5px] text-gray-600">
          Pas de version {LANG_NAME[lang]} : ces comptes recevront le <strong className="font-medium">français</strong>, tel qu’il sera au moment de l’envoi. Remplissez les champs pour traduire.
        </p>
      )}
      <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1fr)_240px]">
        <div className="space-y-4" dir={dir}>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Titre du push</label>
            <input type="text" value={text.title} maxLength={80} readOnly={!editable} onChange={(e) => setField('title', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Message du push</label>
            <textarea value={text.body} maxLength={240} rows={3} readOnly={!editable} onChange={(e) => setField('body', e.target.value)} className={`${inputClass} resize-none`} />
            <p className="mt-1 text-[11px] text-gray-400" dir="ltr">{text.body.length}/240</p>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Objet de l’e-mail</label>
            <input type="text" value={text.subject} maxLength={120} readOnly={!editable} onChange={(e) => setField('subject', e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">Corps de l’e-mail (HTML)</label>
            <textarea value={text.html} rows={9} readOnly={!editable} onChange={(e) => setField('html', e.target.value)} className={`${inputClass} font-mono text-[12px]`} />
          </div>
        </div>
        <div className="space-y-4 text-[13px]">
          <div>
            <p className="mb-1.5 text-[12px] font-semibold text-gray-900">Canaux</p>
            {(['push', 'email'] as CampaignChannel[]).map((c) => (
              <label key={c} className="flex items-center gap-2 py-0.5 text-gray-700">
                <input type="checkbox" disabled={!editable} checked={channels.includes(c)} onChange={() => { setChannels((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c])); setDirty(true); }} className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                {c === 'push' ? 'Notification push' : 'E-mail'}
              </label>
            ))}
          </div>
          <div>
            <p className="mb-1.5 text-[12px] font-semibold text-gray-900">Audience</p>
            <select value={audience} disabled={!editable || audience === 'user'} onChange={(e) => { setAudience(e.target.value as CampaignAudience); setDirty(true); }} className={inputClass}>
              {(audience === 'user' ? (['user'] as CampaignAudience[]) : SELECTABLE_AUDIENCES).map((a) => <option key={a} value={a}>{AUDIENCE_LABEL[a]}</option>)}
            </select>
          </div>
          <div>
            <p className="mb-1.5 text-[12px] font-semibold text-gray-900">Lien ouvert au tap</p>
            <input type="text" value={link} readOnly={!editable} placeholder="dingo://map" onChange={(e) => { setLink(e.target.value); setDirty(true); }} className={`${inputClass} font-mono text-[12px]`} />
          </div>
        </div>
      </div>
    </div>
  );
};

// --- Aperçu --------------------------------------------------------------------------------

const PreviewPane = ({ campaign, token }: { campaign: MarketingCampaign; token: string }) => {
  const [lang, setLang] = useState<CampaignLang>('fr');
  const [html, setHtml] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const text = useMemo(() => {
    const t = campaign.content?.[lang];
    return t && (t.title || '').trim() ? t : campaign.content?.fr;
  }, [campaign, lang]);
  const fallback = lang !== 'fr' && !(campaign.content?.[lang]?.title || '').trim();

  useEffect(() => {
    let cancelled = false;
    setHtml(null);
    setError(null);
    if (!(campaign.channels || []).includes('email')) return undefined;
    getCampaignPreviewHtml(token, campaign.id_campaign, lang).then((h) => { if (!cancelled) setHtml(h); }).catch((e) => { if (!cancelled) setError(e.message || 'Aperçu indisponible.'); });
    return () => { cancelled = true; };
  }, [campaign.id_campaign, campaign.updated_at, campaign.channels, lang, token]);

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="inline-flex rounded-xl border border-gray-200 bg-white p-0.5 text-[13px] font-medium">
          {LANGS.map((l) => (
            <button key={l.code} type="button" onClick={() => setLang(l.code)} className={`rounded-lg px-3.5 py-1.5 transition-colors ${lang === l.code ? 'bg-gray-900 text-white' : 'text-gray-600 hover:bg-gray-50'}`}>{l.label}</button>
          ))}
        </div>
        {fallback && <span className="text-[12.5px] text-gray-500">Pas de version {LANG_NAME[lang]} : le français part.</span>}
      </div>
      <div className="mt-4 grid grid-cols-1 gap-5 xl:grid-cols-[260px_minmax(0,1fr)]">
        {(campaign.channels || []).includes('push') && (
          <div>
            <p className="mb-2 text-[12px] font-semibold text-gray-900">Notification push</p>
            <div className="rounded-2xl bg-gray-900 p-3 text-white" dir={fallback ? 'ltr' : LANGS.find((l) => l.code === lang)?.dir}>
              <div className="flex items-center gap-2 text-[10px] text-gray-300"><span className="inline-block h-4 w-4 rounded bg-indigo-500" /> Dingo · maintenant</div>
              <p className="mt-1.5 text-[13px] font-semibold leading-snug">{text?.title || '-'}</p>
              <p className="mt-0.5 text-[12px] leading-snug text-gray-200">{text?.body || '-'}</p>
            </div>
          </div>
        )}
        {(campaign.channels || []).includes('email') && (
          <div className="min-w-0">
            <p className="mb-2 text-[12px] font-semibold text-gray-900">E-mail · <span className="font-normal text-gray-600">{text?.subject || text?.title}</span></p>
            <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
              {error ? <p className="px-3 py-8 text-center text-sm text-red-700">{error}</p> : html ? <iframe title="Aperçu de l’e-mail" srcDoc={html} sandbox="" className="h-[560px] w-full" /> : <div className="h-[300px] animate-pulse bg-gray-50" />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignPage;
